/**
 * FrameScrubber -- Playback controls for frame-based canvas rendering.
 *
 * Provides: frame slider, play/pause button, frame counter, FPS display,
 * and an optional elapsed-time overlay (rendered via a render prop so the
 * parent can position it over the canvas).
 */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Slider } from '@mui/material';

const FrameScrubber = React.memo(function FrameScrubber({
  currentIndex,
  frameCount,
  fps,
  isPlaying,
  onGoToFrame,
  onTogglePlay,
  frameTimestamps,
  // Optional propagation-window markers. When all four are provided the slider
  // renders three thumbs (end | current | start) with disableSwap so current is
  // constrained between the bounds. The thumb-index→semantic mapping is fixed:
  //   activeThumb 0 -> end (left), 1 -> current, 2 -> start (right).
  propagateStart,
  propagateEnd,
  onPropagateStartChange,
  onPropagateEndChange,
}) {
  const maxIndex = Math.max(0, frameCount - 1);
  const hasMarkers =
    typeof propagateStart === 'number' &&
    typeof propagateEnd === 'number' &&
    typeof onPropagateStartChange === 'function' &&
    typeof onPropagateEndChange === 'function';

  // Local "drag preview" state. While the user is actively dragging, we
  // update only this state so the thumb tracks the cursor smoothly without
  // firing a frame load on every integer step (which would queue 100s of
  // network fetches when scrubbing across the timeline).
  const [draftCurrent, setDraftCurrent] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const [draftStart, setDraftStart] = useState(null);
  const draggingRef = useRef(false);

  const clamp = useCallback((v) => Math.min(maxIndex, Math.max(0, v)), [maxIndex]);

  // Whenever the parent moves (e.g. via Play), drop stale drafts.
  useEffect(() => {
    if (!draggingRef.current) {
      setDraftCurrent(null);
      setDraftEnd(null);
      setDraftStart(null);
    }
  }, [currentIndex, propagateStart, propagateEnd]);

  const effectiveCurrent = draftCurrent ?? currentIndex;
  const effectiveEnd = draftEnd ?? propagateEnd;
  const effectiveStart = draftStart ?? propagateStart;

  const handleSliderChange = useCallback(
    (_, value) => {
      draggingRef.current = true;
      setDraftCurrent(value);
    },
    [],
  );

  const handleSliderCommitted = useCallback(
    (_, value) => {
      draggingRef.current = false;
      setDraftCurrent(null);
      onGoToFrame(value);
    },
    [onGoToFrame],
  );

  const handleMultiChange = useCallback(
    (_, value, activeThumb) => {
      // value is sorted ascending; thumbs are positional: 0=end, 1=current, 2=start.
      draggingRef.current = true;
      if (activeThumb === 0) setDraftEnd(value[0]);
      else if (activeThumb === 1) setDraftCurrent(value[1]);
      else if (activeThumb === 2) setDraftStart(value[2]);
    },
    [],
  );

  const handleMultiCommitted = useCallback(
    (_, value) => {
      draggingRef.current = false;
      // Always commit all three on release; cheap setState if unchanged.
      if (draftEnd !== null) onPropagateEndChange(value[0]);
      if (draftCurrent !== null) onGoToFrame(value[1]);
      if (draftStart !== null) onPropagateStartChange(value[2]);
      setDraftEnd(null);
      setDraftCurrent(null);
      setDraftStart(null);
    },
    [draftEnd, draftCurrent, draftStart, onGoToFrame, onPropagateEndChange, onPropagateStartChange],
  );

  // Derive sorted [end, current, start] for the 3-thumb slider. Clamp into range.
  const triValue = useMemo(
    () => (hasMarkers
      ? [
        clamp(Math.min(effectiveEnd, effectiveStart)),
        clamp(effectiveCurrent),
        clamp(Math.max(effectiveEnd, effectiveStart)),
      ]
      : null),
    [hasMarkers, clamp, effectiveCurrent, effectiveEnd, effectiveStart],
  );

  return (
    <Box sx={{ px: 2, pt: 1, pb: 0.5, bgcolor: '#111', flexShrink: 0 }}>
      {hasMarkers ? (
        <Slider
          value={triValue}
          min={0}
          max={maxIndex}
          step={1}
          onChange={handleMultiChange}
          onChangeCommitted={handleMultiCommitted}
          disableSwap
          size="small"
          valueLabelDisplay="auto"
          valueLabelFormat={(v, idx) => {
            if (idx === 0) return `End: ${v}`;
            if (idx === 2) return `Start: ${v}`;
            return `Frame: ${v}`;
          }}
          sx={{
            color: '#1976d2',
            py: 0.5,
            // Style the bound thumbs (0 = end, 2 = start) distinctly from current.
            '& .MuiSlider-thumb[data-index="0"]': {
              borderRadius: '2px',
              width: 10,
              height: 18,
              backgroundColor: '#ef5350', // end marker
            },
            '& .MuiSlider-thumb[data-index="2"]': {
              borderRadius: '2px',
              width: 10,
              height: 18,
              backgroundColor: '#66bb6a', // start marker
            },
            '& .MuiSlider-thumb[data-index="1"]': {
              backgroundColor: '#fff',
            },
          }}
        />
      ) : (
        <Slider
          value={effectiveCurrent}
          min={0}
          max={maxIndex}
          step={1}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommitted}
          size="small"
          sx={{ color: '#1976d2', py: 0.5 }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left group: play/pause + frame counter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onTogglePlay}
            disabled={frameCount <= 1}
            sx={{
              minWidth: 64,
              fontSize: 11,
              py: 0.25,
              px: 1,
              color: '#aaa',
              borderColor: '#444',
            }}
          >
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
          <Typography variant="caption" sx={{ color: '#666' }}>
            {effectiveCurrent} / {maxIndex}
          </Typography>
          {hasMarkers && (
            <Typography variant="caption" sx={{ color: '#666' }}>
              <Box component="span" sx={{ color: '#66bb6a' }}>● Start</Box> {effectiveStart}
              {' · '}
              <Box component="span" sx={{ color: '#ef5350' }}>● End</Box> {effectiveEnd}
            </Typography>
          )}
        </Box>
        {/* Right group: FPS */}
        <Typography variant="caption" sx={{ color: '#444' }}>
          {fps.toFixed(1)} fps
        </Typography>
      </Box>
    </Box>
  );
});

/**
 * TimestampOverlay -- Positioned absolutely over the canvas by the parent.
 * Shows elapsed time + current frame number.
 */
export function TimestampOverlay({ currentIndex, frameCount, frameTimestamps }) {
  if (frameTimestamps == null || frameTimestamps[currentIndex] === undefined) {
    return null;
  }
  const totalSeconds = frameTimestamps[currentIndex];
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);

  let timeStr = 'T+';
  if (h > 0) timeStr += `${h}h `;
  if (m > 0 || h > 0) timeStr += `${m}m `;
  timeStr += `${s}s`;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        bgcolor: 'rgba(0,0,0,0.65)',
        color: '#eee',
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: 12,
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {timeStr} | Frame {currentIndex} / {Math.max(0, frameCount - 1)}
    </Box>
  );
}

export default FrameScrubber;
